import { useState, useEffect } from 'react';
import { X, CreditCard, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTheme } from './ThemeContext';
import { useModal } from './AppModal';

// Card form component that uses Stripe hooks
function CardForm({ customerId, companyId, connectedAccountId, clientSecret, onSuccess, onCancel }) {
  const { colors } = useTheme();
  const { showError, showSuccess } = useModal();
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setSaving(true);

    try {
      const cardElement = elements.getElement(CardElement);

      // Confirm the SetupIntent
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement
        }
      });

      if (error) {
        showError(error.message);
        setSaving(false);
        return;
      }

      // Save the payment method to our database
      const response = await fetch('/api/save-payment-method', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          companyId,
          setupIntentId: setupIntent.id
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save card');
      }

      showSuccess('Card saved successfully');
      onSuccess(result.card);
    } catch (err) {
      showError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: colors.text,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        '::placeholder': {
          color: colors.textMuted
        }
      },
      invalid: {
        color: colors.error,
        iconColor: colors.error
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{
        background: colors.bgInput,
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1rem'
      }}>
        <CardElement
          options={cardElementOptions}
          onChange={(e) => setCardComplete(e.complete)}
        />
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        color: colors.textMuted,
        fontSize: '0.85rem'
      }}>
        <Lock size={14} />
        <span>Your card is encrypted and stored securely with Stripe</span>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          type="submit"
          disabled={saving || !stripe || !cardComplete}
          style={{
            flex: 1,
            padding: '0.875rem',
            background: (saving || !cardComplete) ? colors.bgInput : colors.success,
            color: (saving || !cardComplete) ? colors.textMuted : '#000',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: (saving || !cardComplete) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          {saving ? (
            <>Saving...</>
          ) : (
            <>
              <CheckCircle size={18} />
              Save Card
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{
            padding: '0.875rem 1.5rem',
            background: colors.bgInput,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function SaveCardModal({ customer, companyId, onClose, onSaved, isDemo = false }) {
  const { colors } = useTheme();
  const { showError } = useModal();
  const [loading, setLoading] = useState(true);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [connectedAccountId, setConnectedAccountId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    initializeStripe();
  }, [customer.id, companyId]);

  const initializeStripe = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create SetupIntent
      const response = await fetch('/api/setup-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: customer.id,
          companyId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to initialize card setup');
      }

      setClientSecret(data.clientSecret);
      setConnectedAccountId(data.connectedAccountId);

      // Load Stripe with the connected account (test key for demo accounts)
      const stripeKey = isDemo
        ? (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST || '')
        : (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51ShJBYJJDak9TtOoaqll0xNqwEsI0nnGNUKrX4HIk1hGgRatwxGYG1UHjk9jMUh3w3Ez9OJt5JO0kyPpOHBqBfbS00AHoP3nbL');
      const stripe = loadStripe(stripeKey, {
        stripeAccount: data.connectedAccountId
      });
      setStripePromise(stripe);
    } catch (err) {
      setError(err.message);
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = (card) => {
    onSaved(card);
    onClose();
  };

  const customerName = customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Customer';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: colors.bgCard,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '450px',
        maxHeight: '90vh',
        overflow: 'auto',
        border: `1px solid ${colors.border}`
      }}>
        {/* Header */}
        <div style={{
          padding: '1.25rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1.25rem'
            }}>
              <CreditCard size={22} color={colors.accent} />
              Save Card on File
            </h2>
            <p style={{ margin: '0.25rem 0 0', color: colors.textMuted, fontSize: '0.85rem' }}>
              {customerName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: colors.textMuted
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: `3px solid ${colors.border}`,
                borderTopColor: colors.accent,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1rem'
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              Initializing secure card entry...
            </div>
          ) : error ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem'
            }}>
              <AlertCircle size={48} color={colors.error} style={{ marginBottom: '1rem' }} />
              <p style={{ color: colors.error, marginBottom: '1rem' }}>{error}</p>
              <button
                onClick={initializeStripe}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: colors.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
            </div>
          ) : stripePromise && clientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CardForm
                customerId={customer.id}
                companyId={companyId}
                connectedAccountId={connectedAccountId}
                clientSecret={clientSecret}
                onSuccess={handleSuccess}
                onCancel={onClose}
              />
            </Elements>
          ) : null}
        </div>
      </div>
    </div>
  );
}
